/**
 * RoleService.ts
 *
 * Service pour gérer les rôles utilisateur avec cache
 * PR-05 : Auth robuste + profil garanti
 */

import { getSupabaseClient } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Cache mémoire pour les vérifications admin
 * Structure : Map<userId, { isAdmin: boolean, timestamp: number }>
 */
const adminCache = new Map<string, { isAdmin: boolean; timestamp: number }>();

/**
 * TTL du cache (5 minutes)
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Vérifie si un utilisateur est admin (avec cache)
 *
 * @param userId - ID de l'utilisateur
 * @param forceRefresh - Forcer le refresh du cache
 * @returns Promise<boolean>
 *
 * @example
 * ```typescript
 * const isAdmin = await RoleService.isAdmin(user.id);
 * ```
 */
export async function isAdmin(userId: string, forceRefresh: boolean = false): Promise<boolean> {
  // Vérifier le cache
  if (!forceRefresh) {
    const cached = adminCache.get(userId);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < CACHE_TTL) {
        logger.debug(`[RoleService] Cache hit pour ${userId} (admin: ${cached.isAdmin})`);
        return cached.isAdmin;
      }
      // Cache expiré, on le supprime
      adminCache.delete(userId);
    }
  }

  const supabase = getSupabaseClient();
  if (!supabase) {
    logger.warn('[RoleService] Supabase non configuré, retourne false');
    return false;
  }

  try {
    // Utiliser la fonction RPC is_admin si disponible, sinon requête directe
    // P3 : RPC `is_admin(user_id uuid)` — la migration utilise `user_id`, pas `uid`.
    const { data: rpcData, error: rpcError } = await supabase.rpc('is_admin', {
      user_id: userId,
    });

    let adminStatus = false;

    if (rpcError) {
      // Si RPC n'existe pas, fallback sur requête directe
      logger.debug('[RoleService] RPC is_admin non disponible, fallback sur requête directe');
      const { data, error } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .single();

      if (error) {
        logger.error(`[RoleService] Erreur lors de la vérification admin pour ${userId}:`, error);
        return false;
      }

      adminStatus = data?.is_admin || false;
    } else {
      adminStatus = rpcData || false;
    }

    // Mettre en cache
    adminCache.set(userId, {
      isAdmin: adminStatus,
      timestamp: Date.now(),
    });

    logger.debug(`[RoleService] ${userId} est ${adminStatus ? 'admin' : 'user'}`);
    return adminStatus;
  } catch (error) {
    logger.error(`[RoleService] Erreur lors de la vérification admin pour ${userId}:`, error);
    return false;
  }
}

/**
 * Invalide le cache pour un utilisateur (ou tous)
 *
 * @param userId - ID de l'utilisateur (optionnel, si non fourni, invalide tout)
 */
export function invalidateCache(userId?: string): void {
  if (userId) {
    adminCache.delete(userId);
    logger.debug(`[RoleService] Cache invalidé pour ${userId}`);
  } else {
    adminCache.clear();
    logger.debug('[RoleService] Cache invalidé pour tous les utilisateurs');
  }
}

/**
 * Récupère la valeur du cache sans requête (peut retourner undefined si non en cache)
 */
export function getCachedAdminStatus(userId: string): boolean | undefined {
  const cached = adminCache.get(userId);
  if (cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_TTL) {
      return cached.isAdmin;
    }
    // Cache expiré
    adminCache.delete(userId);
  }
  return undefined;
}
